import {useAtom} from 'jotai';
import {useState} from 'react';
import {LoadingBar} from '@/components/LoadingBar';
import {useGA4WithParams} from '@/hooks/tracking';
import {AnalysisItem, BasicAnalysisResult, KodataCategoryGroup} from '@/map/@types';
import {hallSalesCategoriesAtom} from '@/map/atom';
import {CategorySelectWindow} from '@/map/components/CategorySelectWindow';
import {Flexbox, FlexFullGrow, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {humanizeNumber} from '@/utils';
import {
  Box, BoxTitle, EmptyBox, SectionContainer, SectionInfoIcon, SectionTitle, ValueFormatText,
  ValueText, WideButton
} from '../StyledComponents';
import {CategorySalesItem} from './CategorySalesItem';
import {InfoWindow} from './InfoWindow';

interface IProps {
  item: AnalysisItem;
  data?: BasicAnalysisResult['hallSales'];
  isPending?: boolean;
  error?: string;
}

export const HallSales = ({item, data, isPending, error}: IProps) => {
  const [openCategoryModal, setOpenCategoryModal] = useState<boolean>();
  const [selectedCategory, setSelctedCategory] = useAtom(
    hallSalesCategoriesAtom
  );

  const tracking = useGA4WithParams();

  const onSelectedCategory = (selected: KodataCategoryGroup[]) => {
    setSelctedCategory(selected);
    setOpenCategoryModal(false);
    tracking('analysis_saleamt', {
      '2depth_category_array': selected.map((i) => i.categoryDetail),
    });
  };

  const [openInfo, setOpenInfo] = useState<boolean>(false);

  const toggleInfoWindow = () => setOpenInfo((prev) => !prev);

  if (isPending || error || !data) {
    return (
      <SectionContainer>
        <SectionTitle>홀 매출</SectionTitle>
        <EmptyBox>
          {isPending ? (
            <LoadingBar color={palette.textSecondary} />
          ) : error ? (
            <span dangerouslySetInnerHTML={{__html: error}} />
          ) : (
            '홀 매출 정보가 없습니다'
          )}
        </EmptyBox>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <SectionTitle>
        홀 매출
        <SectionInfoIcon onClick={toggleInfoWindow} />
        {openInfo && <InfoWindow onClose={toggleInfoWindow} />}
      </SectionTitle>
      <Box>
        <PaddingBox spacing={4}>
          <PaddingBox left={4} top={8} bottom={8}>
            <Flexbox>
              <BoxTitle>총 매출</BoxTitle>
              <FlexFullGrow />
              {data > 0 ? (
                <ValueText>
                  {humanizeNumber(data, true)}
                  <ValueFormatText>&nbsp;원</ValueFormatText>
                </ValueText>
              ) : (
                <ValueText>정보없음</ValueText>
              )}
            </Flexbox>
          </PaddingBox>
          <WideButton radius onClick={() => setOpenCategoryModal(true)}>
            업종선택
          </WideButton>
        </PaddingBox>
        {!!selectedCategory?.length && (
          <PaddingBox top={8}>
            {selectedCategory.map((i) => (
              <CategorySalesItem
                key={i.category + i.categoryDetail}
                category={i}
                item={item}
              />
            ))}
          </PaddingBox>
        )}
      </Box>
      {openCategoryModal && (
        <CategorySelectWindow
          maxCount={6}
          minCount={1}
          defaultOptions={selectedCategory}
          onClose={() => setOpenCategoryModal(false)}
          onComplete={onSelectedCategory}
        />
      )}
    </SectionContainer>
  );
};
